import React, { Component } from 'react';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import '../Pagination.css';
import '../common/MdPage.css';
import * as constantClass from '../fileWithConstants';
import { createPagedGridController } from '../../hooks/pagedGridClassAdapter';
import * as cursadasService from '../../services/cursadas/cursadasService';
import { classifyCursadasError } from '../../services/cursadas/cursadasDto';
import { displayDate } from '../../services/dates/dateService';
import { ConfirmDialog } from '../common/ConfirmDialog';
import {
    buildStandardRowActions,
    MdRowActions,
    MdTableActionsCell,
    MdTableCell,
} from '../common/MdRowActions';
import { PlusIcon, SearchIcon } from '../common/mdPageIcons';

function formatPeriodo(periodoCursadaDescripcion) {
    if (periodoCursadaDescripcion === '1') return 'Primer Cuatrimestre';
    if (periodoCursadaDescripcion === '2') return 'Segundo Cuatrimestre';
    return 'Anual';
}

function canShowGenerarActa(cursada) {
    return (
        new Date(cursada.periodoCursadaFinInscripcion).setHours(0, 0, 0, 0)
        < new Date().setHours(0, 0, 0, 0)
        && cursada.generarActa
    );
}

export class MDCursadas extends Component {
    static displayName = MDCursadas.name;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            searchInput: '',
            perPage: 10,
            paginationData: [],
            pageCount: 0,
            forcePage: 0,
            showErrorMessage: false,
            errorMessage: '',
            successMessage: '',
            deletingId: null,
            pendingDelete: null,
            pendingGenerarActa: null,
            generandoActaId: null,
        };
        this.grid = createPagedGridController({
            fetchPage: (query) => cursadasService.listCursadas(query),
            defaultPageSize: 10,
            onChange: (gridState) => this.applyGridState(gridState),
        });
        this.searchInputRef = React.createRef();
    }

    applyGridState(gridState) {
        this.setState({
            paginationData: gridState.items,
            pageCount: gridState.pageCount,
            searchInput: gridState.search,
            perPage: gridState.pageSize,
            loading: gridState.loading,
            forcePage: gridState.forcePage,
        });
    }

    async componentDidMount() {
        const canAccess = await constantClass.canAccess('WWCursada');
        if (canAccess) {
            this.populateData();
        }
    }

    handlePageClick = (e) => {
        this.grid.handlePageClick(e).catch((error) => this.handleCursadasError(error));
    };

    handleSearchInputChange = (e) => {
        const searchInput = e.target.value;
        const cleared = searchInput.trim() === '';
        this.setState({ searchInput, successMessage: '', showErrorMessage: false, errorMessage: '' });
        if (cleared && this.grid.getState().search.trim() !== '') {
            this.grid.clearSearchIfNeeded(this.grid.getState().search).catch((error) => this.handleCursadasError(error));
        }
    };

    handleSearchSubmit = (e) => {
        e.preventDefault();
        const search = (this.searchInputRef.current
            ? this.searchInputRef.current.value
            : this.state.searchInput).trim();
        this.setState({ searchInput: search });
        this.grid.submitSearch(search).catch((error) => this.handleCursadasError(error));
    };

    handleRemoveItem = (cursada) => {
        if (this.state.deletingId) {
            return;
        }
        this.setState({
            pendingDelete: {
                id: cursada.cursadaCod,
                nombre: cursada.materiaNombre,
                carrera: cursada.carreraNombre
            },
        });
    };

    handleCancelDelete = () => {
        if (this.state.deletingId) {
            return;
        }
        this.setState({ pendingDelete: null });
    };

    handleConfirmDelete = () => {
        const { pendingDelete } = this.state;
        if (!pendingDelete || this.state.deletingId) {
            return;
        }
        this.deleteItem(pendingDelete.id);
    };

    handleGenerarActaRequest = (cursada) => {
        if (this.state.generandoActaId) {
            return;
        }
        this.setState({
            pendingGenerarActa: {
                id: cursada.cursadaCod,
                nombre: cursada.materiaNombre,
                carrera: cursada.carreraNombre
            },
        });
    };

    handleCancelGenerarActa = () => {
        if (this.state.generandoActaId) {
            return;
        }
        this.setState({ pendingGenerarActa: null });
    };

    handleConfirmGenerarActa = () => {
        const { pendingGenerarActa } = this.state;
        if (!pendingGenerarActa || this.state.generandoActaId) {
            return;
        }
        this.generarActa(pendingGenerarActa.id);
    };

    buildRowActions(cursada) {
        const id = cursada.cursadaCod;
        const extraActions = [];

        if (canShowGenerarActa(cursada)) {
            extraActions.push({
                key: 'generar-acta',
                label: 'Generar acta',
                variant: 'outline',
                icon: 'clipboard',
                onClick: () => this.handleGenerarActaRequest(cursada),
                disabled: Boolean(this.state.generandoActaId),
            });
        }

        return buildStandardRowActions({
            editTo: '/update-cur/' + id + '/0',
            onDelete: () => this.handleRemoveItem(cursada),
            deleteDisabled: this.state.loading || this.state.deletingId === id,
            deleteLabel: this.state.deletingId === id ? 'Eliminando...' : 'Borrar',
            detailTo: '/update-cur/' + id + '/1',
            extraActions,
        });
    }

    renderTable(cursadas) {
        return (
            <div className="md-page__table-card">
                <table className="md-page__table md-page__table--actions-fixed md-page__table--wide" aria-labelledby="md-cur-title">
                    <thead>
                        <tr>
                            <th scope="col">Materia</th>
                            <th scope="col">Carrera</th>
                            <th scope="col">Período de Cursada</th>
                            <th scope="col">Año Académico</th>
                            <th scope="col">Fecha Inicio Inscripción</th>
                            <th scope="col">Fecha Fin Inscripción</th>
                            <th scope="col" className="md-page__table__actions-header" aria-label="Acciones" />
                        </tr>
                    </thead>
                    <tbody>
                        {cursadas.map((cursada) => (
                            <tr key={cursada.cursadaCod}>
                                <MdTableCell>{cursada.materiaNombre}</MdTableCell>
                                <MdTableCell>{cursada.carreraNombre}</MdTableCell>
                                <MdTableCell>{formatPeriodo(cursada.periodoCursadaDescripcion)}</MdTableCell>
                                <MdTableCell>{cursada.anioAcademicoDescripcion}</MdTableCell>
                                <MdTableCell>{displayDate(cursada.periodoCursadaInicioInscripcio)}</MdTableCell>
                                <MdTableCell>{displayDate(cursada.periodoCursadaFinInscripcion)}</MdTableCell>
                                <MdTableActionsCell>
                                    <MdRowActions items={this.buildRowActions(cursada)} />
                                </MdTableActionsCell>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const isBusy = this.state.loading;
        const listLoading = this.state.loading && !this.state.deletingId && !this.state.generandoActaId;
        const { pendingDelete, pendingGenerarActa } = this.state;

        const contents = listLoading
            ? <p className="md-page__loading"><em>Cargando datos...</em></p>
            : this.renderTable(this.state.paginationData);

        return (
            <div className="md-page md-cur">
                <div className="md-page__header">
                    <h1 id="md-cur-title" className="md-page__title">Manejar Datos de Cursada</h1>
                </div>

                {!this.state.showErrorMessage ? (
                    <div className="md-page__toolbar">
                        <Link className="md-page__action-link" to="/add-cur">
                            <PlusIcon />
                            Nueva Cursada
                        </Link>
                        <Link className="md-page__action-link" to="/gen-cur">
                            Generar Cursadas
                        </Link>
                    </div>
                ) : null}

                <div className="md-page__feedback" aria-live="polite" role="status">
                    {this.state.showErrorMessage ? (
                        <div className="md-page__error" role="alert">{this.state.errorMessage}</div>
                    ) : null}
                    {this.state.successMessage ? (
                        <div className="md-page__success">{this.state.successMessage}</div>
                    ) : null}
                </div>

                {!this.state.showErrorMessage ? (
                    <>
                        <form className="md-page__search-row" onSubmit={this.handleSearchSubmit}>
                            <div className="md-page__search-wrap">
                                <span className="md-page__search-icon">
                                    <SearchIcon />
                                </span>
                                <input
                                    ref={this.searchInputRef}
                                    className="md-page__search-input"
                                    type="text"
                                    name="search"
                                    autoComplete="off"
                                    disabled={isBusy}
                                    placeholder="Ingrese el período de cursada o la materia"
                                    value={this.state.searchInput}
                                    onChange={this.handleSearchInputChange}
                                />
                            </div>
                            <button type="submit" className="md-page__search-btn" disabled={isBusy}>
                                Buscar
                            </button>
                        </form>

                        {contents}

                        {!listLoading && this.state.pageCount > 1 ? (
                            <div className="md-page__pagination">
                                <ReactPaginate
                                    previousLabel="Anterior"
                                    nextLabel="Siguiente"
                                    breakLabel="..."
                                    breakClassName="break-me"
                                    pageCount={this.state.pageCount}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={this.handlePageClick}
                                    forcePage={this.state.forcePage}
                                    containerClassName="pagination"
                                    subContainerClassName="pages pagination"
                                    activeClassName="active"
                                />
                            </div>
                        ) : null}
                    </>
                ) : null}

                <ConfirmDialog
                    open={Boolean(pendingDelete)}
                    title="Eliminar cursada"
                    message="Esta acción eliminará la cursada y no se puede deshacer."
                    detail={
                        pendingDelete
                            ? `${pendingDelete.nombre} - ${pendingDelete.carrera}`
                            : ''
                    }
                    confirmLabel="Eliminar"
                    cancelLabel="Cancelar"
                    variant="danger"
                    loading={Boolean(this.state.deletingId)}
                    onConfirm={this.handleConfirmDelete}
                    onCancel={this.handleCancelDelete}
                />

                <ConfirmDialog
                    open={Boolean(pendingGenerarActa)}
                    title="Generar acta de cursada"
                    message="¿Desea generar el Acta de Cursada?"
                    detail={
                        pendingGenerarActa
                            ? `${pendingGenerarActa.nombre} - ${pendingGenerarActa.carrera}`
                            : ''
                    }
                    confirmLabel="Generar"
                    cancelLabel="Cancelar"
                    variant="primary"
                    loading={Boolean(this.state.generandoActaId)}
                    onConfirm={this.handleConfirmGenerarActa}
                    onCancel={this.handleCancelGenerarActa}
                />
            </div>
        );
    }

    handleCursadasError(error) {
        const outcome = classifyCursadasError(error);
        if (outcome.type === 'timeout') {
            constantClass.RedirectToLogin();
            return;
        }
        this.setState({
            showErrorMessage: true,
            errorMessage: outcome.message,
            loading: false,
        });
    }

    async populateData() {
        try {
            await this.grid.load();
        } catch (error) {
            this.handleCursadasError(error);
        }
    }

    async deleteItem(id) {
        this.setState({ deletingId: id, showErrorMessage: false, errorMessage: '' });
        try {
            await cursadasService.deleteCursada(id);
            this.setState({ successMessage: 'El registro se eliminó correctamente.', pendingDelete: null });
            await this.grid.reloadAfterDelete(this.state.paginationData.length);
        } catch (error) {
            this.handleCursadasError(error);
        } finally {
            this.setState({ deletingId: null });
        }
    }

    async generarActa(id) {
        this.setState({ generandoActaId: id, showErrorMessage: false, errorMessage: '' });
        try {
            await cursadasService.generarActaCursada(id);
            this.setState({
                successMessage: 'Acta Generada exitosamente, si la desea imprimir vaya al menú de Actas de Cursadas',
                pendingGenerarActa: null,
            });
            await this.grid.reloadAfterDelete();
        } catch (error) {
            this.handleCursadasError(error);
        } finally {
            this.setState({ generandoActaId: null });
        }
    }
}
